import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Add,
  Delete,
  Search,
  Business,
  Inventory,
} from "@mui/icons-material";
import { itemsAPI, suppliersAPI, purchaseAPI } from "../../services/API";
import { useSnackbar } from "notistack";

export const InvNewPurchase = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expectedDelivery, setExpectedDelivery] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Fetch items based on selected supplier and search term
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (selectedSupplier) {
        searchItemsBySupplier();
      } else {
        setItems([]);
      }
    }, 300); // Increased delay for better performance

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, selectedSupplier]);

  const fetchSuppliers = async () => {
    try {
      const response = await suppliersAPI.getAll();
      setSuppliers(response.data.data.suppliers);
    } catch (error) {
      enqueueSnackbar("Error fetching suppliers", { variant: "error" });
    }
  };

  const searchItemsBySupplier = async () => {
    if (searchTerm.trim() === "") {
      setItems([]);
      return;
    }
    if (!selectedSupplier) {
      setItems([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await itemsAPI.getBySupplier(selectedSupplier._id, {
        search: searchTerm,
        limit: 20
      });
      
      setItems(response.data.data.items || []);
      
      if (response.data.data.items.length === 0 && searchTerm.trim()) {
        enqueueSnackbar(`No items found for "${searchTerm}" from ${selectedSupplier.name}`, { 
          variant: "info",
          autoHideDuration: 3000 
        });
      }
    } catch (error) {
      console.error("Search items by supplier error:", error);
      enqueueSnackbar("Error searching items", { variant: "error" });
      setItems([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const addItemToOrder = (item) => {
    const existingOrderItem = orderItems.find(
      (orderItem) => orderItem.item._id === item._id
    );

    if (existingOrderItem) {
      setOrderItems(
        orderItems.map((orderItem) =>
          orderItem.item._id === item._id
            ? { ...orderItem, quantity: orderItem.quantity + 1 }
            : orderItem
        )
      );
    } else {
      // Use supplier's cost price and min order quantity
      const supplierCostPrice = item.supplierDetails?.costPrice || 0;
      const minOrderQuantity = item.supplierDetails?.minOrderQuantity || 1;
      
      setOrderItems([
        ...orderItems,
        {
          item,
          quantity: minOrderQuantity,
          costPrice: supplierCostPrice,
          sellingPrice: 0, // Will be set by user
          mrp: 0, // Will be set by user
          taxRate: 18,
          minOrderQuantity: minOrderQuantity,
        },
      ]);
    }

    setSearchTerm("");
    enqueueSnackbar(`${item.name} added to order`, { variant: "success" });
  };

  const removeItemFromOrder = (itemId) => {
    setOrderItems(orderItems.filter((orderItem) => orderItem.item._id !== itemId));
  };

  // const updateOrderItem = (itemId, field, value) => {
  //   setOrderItems(
  //     orderItems.map((orderItem) =>
  //       orderItem.item._id === itemId
  //         ? { ...orderItem, [field]: parseFloat(value) || 0 }
  //         : orderItem
  //     )
  //   );
  // };
  const updateOrderItem = (itemId, field, value) => {
  setOrderItems(
    orderItems.map((orderItem) => {
      if (orderItem.item._id === itemId) {
        const numericValue = value === '' ? '' : parseFloat(value);
        return { ...orderItem, [field]: numericValue };
      }
      return orderItem;
    })
  );
};


  const updateQuantity = (itemId, quantity) => {
    const orderItem = orderItems.find(orderItem => orderItem.item._id === itemId);
    const minOrderQuantity = orderItem?.minOrderQuantity || 1;

    if (quantity < minOrderQuantity) {
      enqueueSnackbar(`Minimum order quantity is ${minOrderQuantity}`, { variant: "warning" });
      return;
    }

    if (quantity < 1) {
      removeItemFromOrder(itemId);
      return;
    }

    setOrderItems(
      orderItems.map((orderItem) =>
        orderItem.item._id === itemId ? { ...orderItem, quantity } : orderItem
      )
    );
  };

  const calculateTotals = () => {
    const subtotal = orderItems.reduce(
      (sum, orderItem) => sum + orderItem.quantity * orderItem.costPrice,
      0
    );
    const tax = orderItems.reduce(
      (sum, orderItem) =>
        sum + orderItem.quantity * orderItem.costPrice * (orderItem.taxRate / 100),
      0
    );
    const grandTotal = subtotal + tax;

    return { subtotal, tax, grandTotal };
  };

  const handleCreateOrder = async () => {
    if (!selectedSupplier) {
      enqueueSnackbar("Please select a supplier", { variant: "warning" });
      return;
    }

    if (orderItems.length === 0) {
      enqueueSnackbar("Please add items to the order", { variant: "warning" });
      return;
    }

    // Validate all items have required fields
    const invalidItems = orderItems.filter(orderItem => 
      orderItem.costPrice <= 0 || orderItem.sellingPrice <= 0 || orderItem.mrp <= 0
    );

    if (invalidItems.length > 0) {
      enqueueSnackbar("Please set valid prices for all items", { variant: "warning" });
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        supplier: selectedSupplier._id,
        items: orderItems.map((orderItem) => ({
          item: orderItem.item._id,
          quantity: orderItem.quantity,
          costPrice: orderItem.costPrice,
          sellingPrice: orderItem.sellingPrice,
          mrp: orderItem.mrp,
          taxRate: orderItem.taxRate,
        })),
        expectedDeliveryDate: expectedDelivery || undefined,
        notes: notes || undefined,
      };

      await purchaseAPI.create(orderData);
      enqueueSnackbar("Purchase order created successfully!", {
        variant: "success",
      });
      navigate("/hospital/hospital-inventory/purchases");
    } catch (error) {
      enqueueSnackbar("Error creating purchase order", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierChange = (event, newValue) => {
    setSelectedSupplier(newValue);
    setSearchTerm("");
    setItems([]);
    setOrderItems([]);
  };

  const { subtotal, tax, grandTotal } = calculateTotals();

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        New Purchase Order
      </Typography>

      <Grid container spacing={3}>
        {/* Left Column - Supplier & Item Search */}
        <Grid item xs={12} md={8}>
          {/* Supplier Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Supplier Information
              </Typography>
              <Autocomplete
                options={suppliers}
                getOptionLabel={(option) =>
                  `${option.name} (${option.contactPerson})`
                }
                value={selectedSupplier}
                onChange={handleSupplierChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Supplier"
                    placeholder="Search supplier by name"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Business sx={{ mr: 1, color: "text.secondary" }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {selectedSupplier && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2">
                    <strong>Contact:</strong> {selectedSupplier.contactPerson}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {selectedSupplier.phone}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {selectedSupplier.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Lead Time:</strong> {selectedSupplier.leadTime} days
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Item Search */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Add Items
              </Typography>
              
              {!selectedSupplier ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Please select a supplier first to search for items.
                </Alert>
              ) : (
                <>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search items by name, generic name, or brand..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Search sx={{ mr: 1, color: "text.secondary" }} />
                      ),
                      endAdornment: searchLoading && (
                        <CircularProgress size={20} />
                      ),
                    }}
                  />

                  {searchTerm && (
                    <Box sx={{ mt: 2, maxHeight: 300, overflow: "auto" }}>
                      {searchLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                          <CircularProgress size={30} />
                        </Box>
                      ) : items.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <Inventory sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                          <Typography variant="body1" color="text.secondary">
                            No items found for "{searchTerm}"
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Try a different search term
                          </Typography>
                        </Box>
                      ) : (
                        items.map((item) => (
                          <Box
                            key={item._id}
                            sx={{
                              p: 2,
                              mb: 1,
                              border: 1,
                              borderColor: "divider",
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                            onClick={() => addItemToOrder(item)}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {item.name}
                              </Typography>
                              <Chip 
                                label={`₹${item.supplierDetails?.costPrice || 0}`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {item.genericName} • {item.strength} • {item.form}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.brand} • {item.manufacturer}
                            </Typography>
                            {item.supplierDetails?.minOrderQuantity > 1 && (
                              <Typography variant="caption" color="warning.main">
                                Min. order: {item.supplierDetails.minOrderQuantity}
                              </Typography>
                            )}
                          </Box>
                        ))
                      )}
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Order Items
                </Typography>
                {orderItems.length > 0 && (
                  <Chip 
                    label={`${orderItems.length} items`} 
                    color="primary" 
                    variant="outlined" 
                    size="small" 
                  />
                )}
              </Box>

              {orderItems.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Inventory
                    sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="body1" color="text.secondary">
                    {!selectedSupplier 
                      ? "Select a supplier to start adding items" 
                      : "No items in order. Search and add items above."
                    }
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                        <TableCell align="center">Cost Price</TableCell>
                        <TableCell align="center">Selling Price</TableCell>
                        <TableCell align="center">MRP</TableCell>
                        <TableCell align="center">Tax Rate</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderItems.map((orderItem) => (
                        <TableRow key={orderItem.item._id}>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {orderItem.item.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {orderItem.item.genericName}
                              </Typography>
                              {orderItem.minOrderQuantity > 1 && (
                                <Typography variant="caption" color="warning.main">
                                  Min: {orderItem.minOrderQuantity}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  updateQuantity(
                                    orderItem.item._id,
                                    orderItem.quantity - 1
                                  )
                                }
                                disabled={orderItem.quantity <= orderItem.minOrderQuantity}
                              >
                                -
                              </IconButton>
                              <Typography
                                variant="body1"
                                sx={{ minWidth: 30, textAlign: "center" }}
                              >
                                {orderItem.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  updateQuantity(
                                    orderItem.item._id,
                                    orderItem.quantity + 1
                                  )
                                }
                              >
                                +
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              value={orderItem.costPrice}
                              onChange={(e) =>
                                updateOrderItem(
                                  orderItem.item._id,
                                  "costPrice",
                                  e.target.value
                                )
                              }
                              sx={{ width: 90 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              value={
                                orderItem.sellingPrice === 0
                                  ? ""
                                  : orderItem.sellingPrice
                              }
                              onChange={(e) =>
                                updateOrderItem(
                                  orderItem.item._id,
                                  "sellingPrice",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => {
                                if (
                                  e.target.value === "" ||
                                  parseFloat(e.target.value) < 0
                                ) {
                                  updateOrderItem(
                                    orderItem.item._id,
                                    "sellingPrice",
                                    "0"
                                  );
                                }
                              }}
                              sx={{ width: 90 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              value={orderItem.mrp === 0 ? "" : orderItem.mrp}
                              onChange={(e) =>
                                updateOrderItem(
                                  orderItem.item._id,
                                  "mrp",
                                  e.target.value
                                )
                              }
                              onBlur={(e) => {
                                if (
                                  e.target.value === "" ||
                                  parseFloat(e.target.value) < 0
                                ) {
                                  updateOrderItem(
                                    orderItem.item._id,
                                    "mrp",
                                    "0"
                                  );
                                }
                              }}
                              sx={{ width: 90 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              value={orderItem.taxRate}
                              onChange={(e) =>
                                updateOrderItem(
                                  orderItem.item._id,
                                  "taxRate",
                                  e.target.value
                                )
                              }
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight="bold">
                              ₹{(orderItem.quantity * orderItem.costPrice).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              onClick={() => removeItemFromOrder(orderItem.item._id)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Order Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 100 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Order Summary
              </Typography>

              {selectedSupplier && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Supplier
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedSupplier.name}
                  </Typography>
                </Box>
              )}

              <TextField
                fullWidth
                type="date"
                label="Expected Delivery"
                value={expectedDelivery}
                onChange={(e) => setExpectedDelivery(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">₹{subtotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Tax:</Typography>
                  <Typography variant="body2">₹{tax.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6" fontWeight="bold">Total:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    ₹{grandTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                This order will be created as a draft. You can send it to the supplier later.
              </Alert>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleCreateOrder}
                disabled={!selectedSupplier || orderItems.length === 0 || loading}
                sx={{ py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Create Purchase Order - ₹${grandTotal.toFixed(2)}`
                )}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};